import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useState } from 'react';

import Drop from '@/components/common/Dnd/Drop';
import Icon from '@/components/npl/Icon';
import NPLButton from '@/components/npl/NPLButton';

import OptionMenuV2 from '../../OptionMenuV2';
import AddFolderItemSection from '../AddFolderItemSection';
import FolderItems from '../FolderItems';
import { FILE_FOLDER_ITEMS_DROPZONE_TYPE } from '../FolderItemsDropContext';

const className = 'c-PortalContent-FolderItemSectionCard';

export const SECTION_FILES_DROPPABLE_ID_PREFIX = 'section-files-dropzone-';

const FolderItemSectionCard = (props) => {
  const {
    folderItem,
    menuOptions,
    draggableProvided,
    // disableDrop,

    onFolderItemEditClick,
    onAddItemsToFolderList,
    setShowAddContentDiv,
    onFolderItemsDrop,

    isExpanded,
    toggleSectionAccordion,
    folderItemIdsInFocus,
    removeFolderItemFromFocus,
    debug
  } = props;

  const [closeOptions, setCloseOptions] = useState(false);

  const { title, description } = folderItem;
  const sectionFolderItemId = folderItem._id;
  const sectionFolderItemsList = folderItem?.items || [];

  const cardRef = useRef(null);

  const isInFocus = folderItemIdsInFocus?.includes(sectionFolderItemId);

  const droppableId = `${SECTION_FILES_DROPPABLE_ID_PREFIX}${sectionFolderItemId}`;

  const renderAccordionBody = () => (
    <div
      className={classNames('rounded-b-12 transition-all duration-300', {
        'max-h-[2500px] overflow-scroll': isExpanded,
        'max-h-0 overflow-hidden': !isExpanded
      })}>
      <div className="px-8 py-12">
        {/* Add Files To Section */}
        <div className="py-8 pl-40 pr-8">
          <AddFolderItemSection
            showAddContentDiv={true}
            showAddButton={false}
            showSupportTextInDropzone={false}
            // isUploadingStatus={isUploadingStatus}
            onFolderItemsDrop={(files) => {
              onFolderItemsDrop({
                files,
                parentSectionId: sectionFolderItemId
              });
              setShowAddContentDiv(false);
            }}
            onAddUrlListSubmit={({ list }) => {
              onAddItemsToFolderList({
                list,
                parentSectionId: sectionFolderItemId
              });
              setShowAddContentDiv(false);
            }}
            debug={debug}
          />
        </div>

        {/* Section Items */}
        <Drop
          key={droppableId}
          id={droppableId}
          type={FILE_FOLDER_ITEMS_DROPZONE_TYPE}
          isDropDisabled={!isExpanded}
          className="rounded-12 py-12">
          <div className="mt-8 flex flex-col space-y-8">
            <FolderItems
              key={`folder-items-${sectionFolderItemId}`}
              folderItemsList={sectionFolderItemsList}
              parentSectionId={sectionFolderItemId}
            />
          </div>
        </Drop>
      </div>
    </div>
  );

  // Scroll to the section card if it is in focus
  useEffect(() => {
    if (cardRef?.current && isInFocus) {
      cardRef?.current?.scrollIntoView();
    }
  }, [cardRef, isInFocus]);

  // Remove focus on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      if (cardRef?.current && !cardRef.current.contains(event.target)) {
        removeFolderItemFromFocus(sectionFolderItemId);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [cardRef]);

  return (
    <div
      ref={cardRef}
      id={`c-FolderItemSectionCard--${sectionFolderItemId}`}
      className={classNames(
        className,
        'group/section rounded-12 border-1 bg-white-default',
        {
          'border-npl-neutral-light-solid-6': !isInFocus,
          'border-dark-1b': isInFocus
        }
      )}
      onMouseEnter={() => setCloseOptions(false)}
      onMouseLeave={() => setCloseOptions(true)}>
      {/* Accordion Title Section */}
      <div
        className={classNames(
          'relative flex min-h-[78px] items-center justify-between rounded-t-12 bg-npl-neutral-light-solid-2 px-4 py-16 hover:bg-npl-neutral-light-solid-3',
          {
            'rounded-b-12': !isExpanded
          }
        )}>
        {/* Drag Icon, Toggle, Title & Desc */}
        <div className="flex items-center space-x-4">
          {/* Drag Icon */}
          <div
            {...draggableProvided?.dragHandleProps}
            className="invisible rounded-8 py-8 hover:bg-neutral-90 group-hover/section:visible">
            <Icon name="dots-reorder-drag" fillOpacity={0.65} />
          </div>
          {/* Expand/Collapse Toggle Icon */}
          <button
            className={classNames(
              'duration-250 flex h-40 w-40 items-center justify-center transition-all',
              {
                'rotate-[-90deg]': !isExpanded
              }
            )}
            onClick={() => toggleSectionAccordion(sectionFolderItemId)}>
            <Icon name="chevron-down" width="20" height="20" />
          </button>
          {/* Title & Desc */}
          <div className="max-w-[672px]">
            <h3 className="text-label-md font-semibold text-dark-1b">
              {title}
              {debug ? ` | ${sectionFolderItemId}` : ''}
            </h3>
            {description && (
              <p className="mt-2 whitespace-pre-wrap text-body-xs text-dark-1b">
                {description}
              </p>
            )}
          </div>
        </div>
        {/* Edit & Kebab Buttons */}
        <div className="hidden group-hover/section:flex">
          <NPLButton
            rounded
            hierarchy="plain"
            onClick={() => {
              onFolderItemEditClick({ item: folderItem });
            }}
            leadIcon="edit"
            size="md"
          />
          <OptionMenuV2
            options={[...menuOptions]}
            customTriggerClassNames="hover:bg-neutral-90 rounded-full"
            menuIcon={<Icon name="dots-vertical" />}
            close={closeOptions}
          />
        </div>
      </div>
      {/* Accordion Body */}
      {renderAccordionBody()}
    </div>
  );
};

// export default FolderItemSectionCard;
export default React.memo(FolderItemSectionCard);
